import { useMemo, useState } from "react";
import { format, parse } from "date-fns";
import { Plus, X } from "lucide-react";
import { useMutation, useQuery } from "react-query";

import useStore from "../../hooks/store/useStore";
import Mask from "../../components/ui/Mask";
import Text from "../../components/ui/Text";
import Touchable from "../../components/ui/Touchable";
import cn from "../../utils/cn";
import bookDemoFnJP from "../../services/bookDemo/bookDemoFnJP";
import getCalendarJP from "../../services/bookDemo/getCalendarJP";
import Button from "./components/Button";
import DateFieldJP from "./components/DateFieldJP";
import RadioButton from "./components/RadioButton";
import {
  JpDayPreferenceOptions,
  JpSlotLabels,
  JpTimeOptions,
} from "./constants";

type Slot = { date: Date | undefined; time: string | null };

const createEmptySlot = (): Slot => ({ date: undefined, time: null });

export default function BookDemoFormJP({ leadNo }: { leadNo: string }) {
  const { setShowBookDemoDrawer, setShowBookDemoThanks } = useStore(
    (state) => state.bookDemo,
  );
  const { userInformation } = useStore((state) => state.user);
  const { userName } = userInformation || {};

  const [selectedPreference, setSelectedPreference] = useState("");
  const [slots, setSlots] = useState<Slot[]>([createEmptySlot()]);

  const isSpecificDateTime = selectedPreference === "0";
  const isMaxSlots = slots.length >= 3;
  const isSubmitDisabled =
    !selectedPreference ||
    (isSpecificDateTime && slots.some((slot) => !slot.date || !slot.time));

  const { data, isLoading: isCalendarLoading } = useQuery({
    queryKey: ["getCalendarJP"],
    queryFn: getCalendarJP,
    enabled: isSpecificDateTime,
  });
  const mutation = useMutation({
    mutationFn: bookDemoFnJP,
    onSuccess: () => {
      setShowBookDemoDrawer(false);
      setShowBookDemoThanks(true);
    },
  });

  const { availableDateKeys, fromDate, toDate } = useMemo(() => {
    const days: string[] = data?.days ?? data?.Days ?? [];
    const dates = days.map((day) => parse(day, "yyyyMMdd", new Date()));
    const times = dates.map((date) => date.getTime());

    return {
      availableDateKeys: new Set(days),
      fromDate: times.length ? new Date(Math.min(...times)) : undefined,
      toDate: times.length ? new Date(Math.max(...times)) : undefined,
    };
  }, [data]);

  const updateSlot = (index: number, patch: Partial<Slot>) => {
    setSlots((prev) =>
      prev.map((slot, i) => (i === index ? { ...slot, ...patch } : slot)),
    );
  };

  const removeSlot = (index: number) => {
    setSlots((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const fields: Record<string, string> = {
      hopeKbn: selectedPreference,
      leadNo,
    };

    if (isSpecificDateTime) {
      slots.forEach((slot, index) => {
        if (!slot.date || !slot.time) return;
        const n = index + 1;
        fields[`hopeDate${n}`] = format(slot.date, "yyyyMMdd");
        fields[`hopeTime${n}`] = slot.time;
      });
    }

    mutation.mutate(fields);
  };

  return (
    <div className="flex w-full flex-col items-center">
      <div className="flex w-full max-w-[310px] flex-col">
        <div className="flex flex-col gap-3 pb-10">
          <Text className="text-[20px] leading-[26px] text-grey8">
            無料体験のご予約
          </Text>

          <div>
            <Text normal>
              {`${userName}様の担当アドバイザーがご自宅に教材をお持ちしてご説明いたします。`}
            </Text>
            <Text normal>
              以下のフォームよりご希望の日時を選んでご予約ください。
            </Text>
          </div>
        </div>

        <div className="flex flex-col gap-3 pb-6">
          {JpDayPreferenceOptions.map(({ label, value }) => (
            <RadioButton
              key={value}
              text={label}
              checked={selectedPreference === value}
              onClick={() => setSelectedPreference(value)}
            />
          ))}

          {isSpecificDateTime ? (
            <>
              <Text normal className="text-black6">
                *本日から3日後以降、2ヶ月以内の日付をご指定いただけます。
              </Text>

              {slots.map((slot, index) => (
                <div key={JpSlotLabels[index]} className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <Text className="font-medium text-black6">
                      {JpSlotLabels[index]}
                    </Text>
                    {index > 0 ? (
                      <Touchable onClick={() => removeSlot(index)}>
                        <X className="size-6 text-black6" />
                      </Touchable>
                    ) : null}
                  </div>

                  <DateFieldJP
                    date={slot.date}
                    onChange={(date) => updateSlot(index, { date })}
                    fromDate={fromDate}
                    toDate={toDate}
                    isLoading={isCalendarLoading}
                    disabled={(date) =>
                      !availableDateKeys.has(format(date, "yyyyMMdd"))
                    }
                  />

                  <div className="grid grid-cols-2 gap-3">
                    {JpTimeOptions.map(({ label, value }) => (
                      <RadioButton
                        key={value}
                        text={label}
                        checked={slot.time === value}
                        onClick={() => updateSlot(index, { time: value })}
                      />
                    ))}
                  </div>
                </div>
              ))}

              <Touchable
                className={cn(
                  "flex h-6 items-center justify-end gap-0.5",
                  isMaxSlots
                    ? "pointer-events-none text-grey4"
                    : "text-orange2",
                )}
                onClick={() => setSlots((prev) => [...prev, createEmptySlot()])}
              >
                <Text className="font-medium text-inherit">別の時間を追加</Text>
                <Plus className="size-6" />
              </Touchable>
            </>
          ) : null}

          <div>
            <Text normal className="text-black6">
              *実際にお伺いする日程は、ご希望日を確認の上、担当アドバイザーより日程調整・確定のお電話をさせていただきます。土日祝日は、お申し込み内容の確認に時間を要する場合がございます。
            </Text>
            <Text normal className="text-black6">
              *無料体験は、既にディズニー英語システムをご購入いただいているお客さまは対象外となります。
            </Text>
          </div>
        </div>
      </div>
      <div
        className="sticky bottom-0 w-full bg-pink1 p-3"
        style={{ boxShadow: "2px 2px 32px rgba(29, 29, 29, 0.10)" }}
      >
        <Button
          text="送信"
          onClick={handleSubmit}
          isLoading={mutation.isLoading}
          disabled={isSubmitDisabled || mutation.isLoading}
        />
      </div>

      {mutation.isLoading ? <Mask /> : null}
    </div>
  );
}
